'use client';

import { API_HEADERS } from '@trucksmarter/shared';
import env from '~/env.public';
import Endpoint, { FileUploadEndpoint } from '../endpoints';
import {
  ErrorResponse,
  getErrorDisplayMessageOrDefault,
} from '../error-response';
import { openSnackbar } from '~/app/_ui/Dallas/Snackbar/DallasSnackbarService';
import DallasSvgIconWarningOutlined from '~/app/_ui/Dallas/Icons/DallasSvgIconWarningOutlined';
import { COMMON_REQUEST_HEADERS } from '../meta';
import { STATUS_CODE_OK, STATUS_CODE_UNAUTHORIZED } from '../http-status-codes';
import redirectToLogin from './redirect-to-login';

const baseURL = env.USE_API_PROXY ? '/api' : env.API_URL;

interface HandleResponseOptions {
  bypassErrorSnackbar?: boolean;
  bypassRedirectToLoginIfUnauthorized?: boolean;
  defaultErrorMessage?: string;
}

interface RequestOptions extends HandleResponseOptions {
  signal?: AbortSignal;
  fetchWithWafToken?: boolean;
  /** Additional headers to include in the request. */
  headers?: Record<string, string>;
}

type RequestResponse =
  | {
      ok: true;
      text: () => Promise<string>;
    }
  | {
      ok: false;
      json: () => Promise<ErrorResponse>;
    };

async function handleResponse<T>(
  request: Promise<RequestResponse>,
  options: HandleResponseOptions,
) {
  const {
    bypassErrorSnackbar = false,
    bypassRedirectToLoginIfUnauthorized = false,
    defaultErrorMessage,
  } = options;

  try {
    const response = await request;

    if (!response.ok) {
      const error: ErrorResponse = await response.json();

      if (!bypassErrorSnackbar) {
        openSnackbar({
          color: 'error',
          addOnStart: <DallasSvgIconWarningOutlined />,
          header: getErrorDisplayMessageOrDefault(error, defaultErrorMessage),
          autoDismiss: true,
        });
      }

      throw error;
    }

    const text = await response.text();

    // xhr responses return text in the following format: "{\"success\":false,\"load_count\":144}"
    // which needs to be cleaned of the outer quotes and backslashes to be parsed.
    if (text[0] === '"' && text[text.length - 1] === '"') {
      const cleanedText = text
        .substring(1, text.length - 1)
        .replaceAll(/\\/g, '');
      return JSON.parse(cleanedText === '' ? 'null' : cleanedText) as T;
    }

    // Sometimes server endpoints respond with an empty response body which
    // causes JSON.parse / response.json() to throw an error -- this emulates
    // the server responding with `null`.
    return JSON.parse(text === '' ? 'null' : text) as T;
  } catch (error: unknown) {
    if (
      error != null &&
      typeof error === 'object' &&
      'status' in error &&
      error.status === STATUS_CODE_UNAUTHORIZED &&
      !bypassRedirectToLoginIfUnauthorized
    ) {
      redirectToLogin();
    }

    return Promise.reject(error);
  }
}

interface WindowWithWafIntegration {
  AwsWafIntegration?: {
    fetch: typeof fetch;
  };
}

export async function makeClientSideRequest<B, T>(
  endpoint: Endpoint,
  body?: B,
  options: RequestOptions = {},
): Promise<T> {
  const { signal, ...handleRequestOptions } = options;

  const wafFetch = (window as WindowWithWafIntegration).AwsWafIntegration
    ?.fetch;
  const fetcher: typeof fetch =
    options.fetchWithWafToken && wafFetch != null ? wafFetch : fetch;
  return handleResponse<T>(
    fetcher(`${baseURL}${endpoint}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        [API_HEADERS.requestOrigin]: 'browser',
        ...COMMON_REQUEST_HEADERS,
        ...options.headers,
      },
      signal,
      credentials: 'include',
      body: body && JSON.stringify(body),
    }),
    handleRequestOptions,
  );
}

const MAX_PROGRESS = 100;

export interface FileUploadRequestOptions extends RequestOptions {
  data?: { [key: string]: string };
  onProgress?: (percentage: number) => void;
}

export async function makeClientSideFileUploadRequest<T>(
  endpoint: FileUploadEndpoint,
  files: { [name: string]: File[] },
  options: FileUploadRequestOptions = {},
): Promise<T> {
  const { signal, onProgress, ...handleResponseOptions } = options;
  const formData = new FormData();
  Object.entries(options.data ?? {}).forEach(([key, value]) => {
    formData.append(key, value);
  });
  Object.entries(files).forEach(([name, files]) =>
    files.forEach(async (file) => formData.append(name, file, file.name)),
  );

  return handleResponse<T>(
    new Promise<RequestResponse>((resolve) => {
      const xhr = new XMLHttpRequest();

      xhr.withCredentials = true;

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress?.(Math.floor((event.loaded / event.total) * MAX_PROGRESS));
        }
      };

      xhr.onloadend = () => {
        if (xhr.status === STATUS_CODE_OK) {
          resolve({
            ok: true,
            text: () => Promise.resolve(JSON.stringify(xhr.response)),
          });
        } else {
          resolve({
            ok: false,
            json: () =>
              typeof xhr.response === 'string'
                ? Promise.resolve(JSON.parse(xhr.response))
                : Promise.resolve(xhr.status),
          });
        }
      };

      xhr.open('POST', `${baseURL}${endpoint}`, true);
      Object.entries(COMMON_REQUEST_HEADERS).forEach(([value, key]) => {
        xhr.setRequestHeader(key, value);
      });
      xhr.send(formData);

      signal?.addEventListener('abort', () => xhr.abort());
    }),
    handleResponseOptions,
  );
}
